type JsonRecord = Record<string, unknown>;

export type OptimisticEntityType = 'missions' | 'sessions';

export interface EntityMutationPatch {
  entityType: OptimisticEntityType;
  entityId: string;
  fields: JsonRecord;
}

export interface EntityMutationOverlay extends EntityMutationPatch {
  token: string;
  epoch: number;
  committed: boolean;
  createdAt: number;
  canonical?: JsonRecord;
}

const overlaysByField = new Map<string, EntityMutationOverlay>();
let epoch = 0;

function text(value: unknown): string {
  return typeof value === 'string' ? value : value == null ? '' : String(value);
}

function entityIdOf(value: JsonRecord): string {
  return text(value['fibery/id'] || value.id);
}

function fieldKey(overlay: EntityMutationPatch, field: string): string {
  return `${overlay.entityType}:${overlay.entityId}:${field}`;
}

function activeFields(overlay: EntityMutationOverlay): string[] {
  return Object.keys(overlay.fields).filter(
    (field) => overlaysByField.get(fieldKey(overlay, field))?.token === overlay.token,
  );
}

function referenceId(value: unknown): string {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return '';
  const record = value as JsonRecord;
  return text(record['fibery/id'] || record.id);
}

function normalizeDate(value: unknown): string {
  const raw = text(value).trim();
  if (!raw) return '';
  const parsed = Date.parse(raw);
  return Number.isNaN(parsed) ? raw : new Date(parsed).toISOString();
}

function numberValue(value: unknown): number {
  const parsed = typeof value === 'number' ? value : Number(String(value ?? '').replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalizePricingLine(value: unknown): JsonRecord {
  const line = value && typeof value === 'object' && !Array.isArray(value) ? value as JsonRecord : {};
  return {
    label: text(line.label || line.name).trim(),
    billing_uo: text(line.billing_uo || line.unite_oeuvre).trim().toUpperCase(),
    qty: numberValue(line.qty ?? line.volume),
    unit_price_ht: numberValue(line.unit_price_ht ?? line.tjm ?? line.tjm_ou_th),
    production_mode: text(line.production_mode).trim().toUpperCase() || 'AUTO',
    production_jh_per_unit: numberValue(line.production_jh_per_unit),
    production_jh_total_override: numberValue(line.production_jh_total_override),
    is_billable: line.is_billable !== false,
    // A line saved without a role comes back « UNKNOWN »: the same line.
    line_role: text(line.line_role).trim().toUpperCase().replace(/^UNKNOWN$/, ''),
    contract_line_id: text(line.contract_line_id).trim(),
    contract_source: text(line.contract_source).trim(),
  };
}

function stable(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stable).join(',')}]`;
  if (value && typeof value === 'object') {
    return `{${Object.entries(value as JsonRecord)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, entry]) => `${JSON.stringify(key)}:${stable(entry)}`)
      .join(',')}}`;
  }
  return JSON.stringify(value) ?? String(value);
}

function projectedValue(entity: JsonRecord, field: string): unknown {
  if (field === 'pricing_lines') {
    const summary = entity.pricing_summary && typeof entity.pricing_summary === 'object' && !Array.isArray(entity.pricing_summary)
      ? entity.pricing_summary as JsonRecord
      : {};
    return Array.isArray(entity.pricing_lines)
      ? entity.pricing_lines
      : Array.isArray(summary.lines)
        ? summary.lines
        : [];
  }
  return entity[field];
}

function fieldMatches(entity: JsonRecord, field: string, expected: unknown): boolean {
  const actual = projectedValue(entity, field);
  if (field === 'pricing_lines') {
    const expectedLines = Array.isArray(expected) ? expected.map(normalizePricingLine) : [];
    const actualLines = Array.isArray(actual) ? actual.map(normalizePricingLine) : [];
    return stable(actualLines) === stable(expectedLines);
  }
  const expectedReference = referenceId(expected);
  if (expectedReference) return referenceId(actual) === expectedReference;
  if (expected === null) return actual == null || referenceId(actual) === '';
  if (/date|start|end/i.test(field)) return normalizeDate(actual) === normalizeDate(expected);
  if (typeof expected === 'number') return Math.abs(numberValue(actual) - expected) <= 1e-6;
  return stable(actual) === stable(expected);
}

function projectionFields(overlay: EntityMutationOverlay, fields: string[]): JsonRecord {
  const canonical = overlay.canonical || {};
  return Object.fromEntries(fields.map((field) => {
    if (field === 'pricing_lines') {
      const canonicalSummary = canonical.pricing_summary && typeof canonical.pricing_summary === 'object' && !Array.isArray(canonical.pricing_summary)
        ? canonical.pricing_summary as JsonRecord
        : {};
      const canonicalLines = Array.isArray(canonical.pricing_lines)
        ? canonical.pricing_lines
        : Array.isArray(canonicalSummary.lines)
          ? canonicalSummary.lines
          : undefined;
      return [field, canonicalLines ?? overlay.fields[field]];
    }
    return [field, Object.prototype.hasOwnProperty.call(canonical, field) ? canonical[field] : overlay.fields[field]];
  }));
}

function lineProductionJh(line: JsonRecord): number {
  const override = numberValue(line.production_jh_total_override);
  if (override > 0) return override;
  const qty = numberValue(line.qty ?? line.volume);
  const explicit = numberValue(line.production_jh_per_unit);
  if (explicit > 0) return qty * explicit;
  const unit = text(line.billing_uo || line.unite_oeuvre).toUpperCase();
  return unit === 'H' ? qty / 6 : unit === 'JH' ? qty : 0;
}

function withPricingSummary(entity: JsonRecord, entityType: OptimisticEntityType, lines: unknown): JsonRecord {
  if (!Array.isArray(lines)) return entity;
  const billable = lines.filter((raw) => !(raw && typeof raw === 'object' && !Array.isArray(raw)) || (raw as JsonRecord).is_billable !== false);
  const normalized = billable.map((raw) => raw && typeof raw === 'object' && !Array.isArray(raw) ? raw as JsonRecord : {});
  const units = Array.from(new Set(normalized.map((line) => text(line.billing_uo || line.unite_oeuvre).toUpperCase()).filter(Boolean)));
  const amount = normalized.reduce((sum, line) => sum + numberValue(line.qty ?? line.volume) * numberValue(line.unit_price_ht ?? line.tjm ?? line.tjm_ou_th), 0);
  const quantity = normalized.reduce((sum, line) => sum + numberValue(line.qty ?? line.volume), 0);
  const productionJh = normalized.reduce((sum, line) => sum + lineProductionJh(line), 0);
  const existing = entity.pricing_summary && typeof entity.pricing_summary === 'object' && !Array.isArray(entity.pricing_summary)
    ? entity.pricing_summary as JsonRecord
    : {};
  const defaultRole = entityType === 'missions' ? 'CONTRACT' : 'DELIVERY';
  // Same rule as scopedPricingLines: an UNKNOWN line (the live schema stores
  // no role) belongs to the edited entity unless a line is explicitly scoped.
  const hasExplicitDefault = normalized.some((line) => text(line.line_role).toUpperCase() === defaultRole);
  const roleOf = (line: JsonRecord) => {
    const role = text(line.line_role).toUpperCase();
    if (!role || (role === 'UNKNOWN' && !hasExplicitDefault)) return defaultRole;
    return role;
  };
  const contractAmount = normalized
    .filter((line) => roleOf(line) === 'CONTRACT')
    .reduce((sum, line) => sum + numberValue(line.qty ?? line.volume) * numberValue(line.unit_price_ht ?? line.tjm ?? line.tjm_ou_th), 0);
  const deliveryAmount = normalized
    .filter((line) => roleOf(line) === 'DELIVERY')
    .reduce((sum, line) => sum + numberValue(line.qty ?? line.volume) * numberValue(line.unit_price_ht ?? line.tjm ?? line.tjm_ou_th), 0);
  const allocated = entityType === 'missions' ? numberValue(existing.delivery_allocated_ht ?? existing.amount_ht_sessions) : deliveryAmount;
  return {
    ...entity,
    pricing_summary: {
      ...existing,
      pricing_mode: lines.length > 1 || units.length > 1 ? 'DETAILLE' : 'SIMPLE',
      lines,
      amount_ht_total: amount,
      display_uo: units.length === 1 ? units[0] : units.length > 1 ? 'MIXTE' : '',
      display_qty: units.length === 1 ? quantity : null,
      display_unit_price_ht: normalized.length === 1 ? numberValue(normalized[0].unit_price_ht ?? normalized[0].tjm) : null,
      production_load_jh_total: productionJh,
      ...(entityType === 'missions' ? {
        contract_amount_ht: contractAmount,
        amount_ht_standalone: contractAmount,
        delivery_allocated_ht: allocated,
        contract_remaining_ht: contractAmount - allocated,
      } : {
        delivery_allocated_ht: deliveryAmount,
      }),
    },
  };
}

function withSessionEventAliases(entity: JsonRecord, fields: JsonRecord): JsonRecord {
  if (!isSessionEvent(entity)) return entity;
  const next = { ...entity };
  if (Object.prototype.hasOwnProperty.call(fields, 'name')) next.title = fields.name;
  if (Object.prototype.hasOwnProperty.call(fields, 'date_debut')) next.start = fields.date_debut;
  if (Object.prototype.hasOwnProperty.call(fields, 'date_fin')) next.end = fields.date_fin;
  if (Object.prototype.hasOwnProperty.call(fields, 'sous_traitant')) {
    next.resource_id = referenceId(fields.sous_traitant);
    const value = fields.sous_traitant;
    next.resource_name = value && typeof value === 'object' && !Array.isArray(value) ? text((value as JsonRecord).name) : '';
  }
  if (Object.prototype.hasOwnProperty.call(fields, 'mission')) {
    next.mission_id = referenceId(fields.mission);
    const value = fields.mission;
    next.mission_name = value && typeof value === 'object' && !Array.isArray(value) ? text((value as JsonRecord).name) : '';
  }
  return next;
}

function isSessionEvent(entity: JsonRecord): boolean {
  return text(entity.entity_type).toLowerCase() === 'sessions' || text(entity.kind).toLowerCase() === 'session';
}

/** Id given to a line created by a save that the server has not confirmed yet. */
export const PENDING_LINE_PREFIX = 'pending:';

export function isPendingLineId(id: unknown): boolean {
  return text(id).startsWith(PENDING_LINE_PREFIX);
}

/**
 * The lines a save sends, laid over the lines on screen: each one keeps what
 * only the server computes (monthly plan, placed / remaining, amount). Sent
 * lines alone carry none of it, and the plan looked erased until the next read
 * (Pascal, 29/09/2026: « N'efface plus rien en prod »). A new line shows at
 * once, marked pending, until the server confirms it.
 */
function mergePricingLines(entity: JsonRecord, lines: unknown, token: string): unknown {
  if (!Array.isArray(lines)) return lines;
  const existing = Array.isArray(entity.pricing_lines) ? entity.pricing_lines as unknown[] : [];
  const byId = new Map<string, JsonRecord>();
  existing.forEach((raw) => {
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      const id = text((raw as JsonRecord).id || (raw as JsonRecord)['fibery/id']);
      if (id) byId.set(id, raw as JsonRecord);
    }
  });
  return lines.map((raw, index) => {
    const line = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw as JsonRecord : {};
    const amount = numberValue(line.qty ?? line.volume) * numberValue(line.unit_price_ht ?? line.tjm ?? line.tjm_ou_th);
    const id = text(line.id || line['fibery/id']);
    const current = id ? byId.get(id) : undefined;
    if (current) return { ...current, ...line, amount_ht: amount };
    if (id) return { ...line, amount_ht: amount };
    return { ...line, id: `${PENDING_LINE_PREFIX}${token}:${index}`, amount_ht: amount, plan_rows: [], pending: true };
  });
}

function patchEntity(entity: JsonRecord, overlay: EntityMutationOverlay, fields: string[], token?: string): JsonRecord {
  if (entityIdOf(entity) !== overlay.entityId || fields.length === 0) return entity;
  const projected = projectionFields(overlay, fields);
  if (Object.prototype.hasOwnProperty.call(projected, 'pricing_lines') && projected.pricing_lines === overlay.fields.pricing_lines) {
    projected.pricing_lines = mergePricingLines(entity, projected.pricing_lines, overlay.token);
  }
  let next: JsonRecord = { ...entity, ...projected };
  if (overlay.entityType === 'sessions') next = withSessionEventAliases(next, projected);
  if (Object.prototype.hasOwnProperty.call(projected, 'pricing_lines')) {
    next = withPricingSummary(next, overlay.entityType, projected.pricing_lines);
  }
  if (token) {
    next._optimistic_entity_fields = {
      ...(entity._optimistic_entity_fields && typeof entity._optimistic_entity_fields === 'object' && !Array.isArray(entity._optimistic_entity_fields)
        ? entity._optimistic_entity_fields as JsonRecord
        : {}),
      ...Object.fromEntries(fields.map((field) => [field, token])),
    };
  }
  return next;
}

function mapEntityArrays(payload: unknown, mapper: (entity: JsonRecord) => JsonRecord): unknown {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return payload;
  const source = payload as JsonRecord;
  const patchArray = (value: unknown) => Array.isArray(value)
    ? value.map((entry) => entry && typeof entry === 'object' && !Array.isArray(entry) ? mapper(entry as JsonRecord) : entry)
    : value;
  const next: JsonRecord = { ...source, entities: patchArray(source.entities) };
  if (!source.lookups || typeof source.lookups !== 'object' || Array.isArray(source.lookups)) return next;
  const lookups = source.lookups as JsonRecord;
  const nextLookups: JsonRecord = {
    ...lookups,
    missions: patchArray(lookups.missions),
    sessions: patchArray(lookups.sessions),
  };
  if (lookups.sessions_by_mission && typeof lookups.sessions_by_mission === 'object' && !Array.isArray(lookups.sessions_by_mission)) {
    nextLookups.sessions_by_mission = Object.fromEntries(
      Object.entries(lookups.sessions_by_mission as JsonRecord).map(([missionId, sessions]) => [missionId, patchArray(sessions)]),
    );
  }
  for (const key of ['session_meta', 'sessionMeta']) {
    if (lookups[key] && typeof lookups[key] === 'object' && !Array.isArray(lookups[key])) {
      const meta = lookups[key] as JsonRecord;
      nextLookups[key] = { ...meta, entities: patchArray(meta.entities) };
    }
  }
  next.lookups = nextLookups;
  return next;
}

function payloadContainsField(payload: unknown, overlay: EntityMutationOverlay, field: string): boolean {
  const canonical = projectionFields(overlay, [field])[field];
  let found = false;
  let everyProjectionMatches = true;
  mapEntityArrays(payload, (entity) => {
    if (entityIdOf(entity) !== overlay.entityId) return entity;

    const summary = entity.pricing_summary && typeof entity.pricing_summary === 'object' && !Array.isArray(entity.pricing_summary)
      ? entity.pricing_summary as JsonRecord
      : {};
    const eventAliases: Record<string, string[]> = {
      name: ['title'],
      date_debut: ['start'],
      date_fin: ['end'],
      sous_traitant: ['resource_id'],
      mission: ['mission_id'],
    };
    const aliases = isSessionEvent(entity) ? eventAliases[field] || [] : [];
    const carriesField = Object.prototype.hasOwnProperty.call(entity, field)
      || (field === 'pricing_lines' && (
        Array.isArray(entity.pricing_lines)
        || Object.prototype.hasOwnProperty.call(summary, 'lines')
      ))
      || aliases.some((alias) => Object.prototype.hasOwnProperty.call(entity, alias));
    if (!carriesField) return entity;

    found = true;
    let matches = fieldMatches(entity, field, canonical);
    if (!Object.prototype.hasOwnProperty.call(entity, field) && aliases.length) {
      if (field === 'sous_traitant' || field === 'mission') {
        matches = aliases.some((alias) => text(entity[alias]) === referenceId(canonical));
      } else {
        matches = aliases.some((alias) => fieldMatches({ [field]: entity[alias] }, field, canonical));
      }
    }
    if (!matches) everyProjectionMatches = false;
    return entity;
  });
  return found && everyProjectionMatches;
}

export function beginEntityMutationOverlay(patch: EntityMutationPatch): EntityMutationOverlay {
  epoch += 1;
  const overlay: EntityMutationOverlay = {
    ...patch,
    fields: { ...patch.fields },
    token: `entity-${epoch}-${Date.now()}`,
    epoch,
    committed: false,
    createdAt: Date.now(),
  };
  Object.keys(overlay.fields).forEach((field) => overlaysByField.set(fieldKey(overlay, field), overlay));
  return overlay;
}

export function commitEntityMutationOverlay(overlay: EntityMutationOverlay, canonical?: JsonRecord): void {
  overlay.committed = true;
  if (canonical) overlay.canonical = canonical;
}

export function failEntityMutationOverlay(overlay: EntityMutationOverlay): void {
  activeFields(overlay).forEach((field) => overlaysByField.delete(fieldKey(overlay, field)));
}

export function failEntityMutationOverlayFields(overlay: EntityMutationOverlay, fields: string[]): void {
  fields.forEach((field) => {
    const key = fieldKey(overlay, field);
    if (overlaysByField.get(key)?.token === overlay.token) overlaysByField.delete(key);
  });
}

export function patchEntityMutationPayload(payload: unknown, overlay: EntityMutationOverlay, token?: string): unknown {
  return mapEntityArrays(payload, (entity) => patchEntity(entity, overlay, activeFields(overlay), token));
}

export function mergeCanonicalEntityPayload(payload: unknown, overlay: EntityMutationOverlay, canonical: JsonRecord): unknown {
  const canonicalId = entityIdOf(canonical) || overlay.entityId;
  const merged = mapEntityArrays(payload, (entity) => (
    entityIdOf(entity) === canonicalId ? { ...entity, ...canonical } : entity
  ));
  commitEntityMutationOverlay(overlay, canonical);
  return applyEntityMutationOverlays(merged, { confirmCommitted: false });
}

/** Keep local entity writes over slow/stale tab projections until a GET confirms each field. */
export function applyEntityMutationOverlays(
  payload: unknown,
  options: { confirmCommitted?: boolean } = {},
): unknown {
  let next = payload;
  const now = Date.now();
  const activeOverlays = Array.from(new Map(
    [...overlaysByField.values()].map((overlay) => [overlay.token, overlay]),
  ).values()).sort((left, right) => left.epoch - right.epoch);
  activeOverlays.forEach((overlay) => {
    let fields = activeFields(overlay);
    if (options.confirmCommitted !== false && overlay.committed) {
      fields.filter((field) => payloadContainsField(next, overlay, field)).forEach((field) => {
        overlaysByField.delete(fieldKey(overlay, field));
      });
      fields = activeFields(overlay);
    }
    if (overlay.committed && now - overlay.createdAt > 5 * 60_000) {
      fields.forEach((field) => overlaysByField.delete(fieldKey(overlay, field)));
      return;
    }
    next = mapEntityArrays(next, (entity) => patchEntity(entity, overlay, fields, overlay.token));
  });
  return next;
}

export function rollbackEntityMutationPayload(
  currentPayload: unknown,
  snapshotPayload: unknown,
  overlay: EntityMutationOverlay,
  selectedFields?: string[],
): unknown {
  const previousEntities: JsonRecord[] = [];
  mapEntityArrays(snapshotPayload, (entity) => {
    if (entityIdOf(entity) === overlay.entityId) previousEntities.push(entity);
    return entity;
  });
  if (!previousEntities.length) return currentPayload;
  return mapEntityArrays(currentPayload, (entity) => {
    if (entityIdOf(entity) !== overlay.entityId) return entity;
    const previousEntity = previousEntities.find((candidate) => isSessionEvent(candidate) === isSessionEvent(entity))
      || previousEntities[0];
    const tokens = entity._optimistic_entity_fields && typeof entity._optimistic_entity_fields === 'object' && !Array.isArray(entity._optimistic_entity_fields)
      ? entity._optimistic_entity_fields as JsonRecord
      : {};
    const allowedFields = selectedFields ? new Set(selectedFields) : null;
    const rollbackFields = Object.keys(overlay.fields).filter(
      (field) => (!allowedFields || allowedFields.has(field)) && text(tokens[field]) === overlay.token,
    );
    if (!rollbackFields.length) return entity;
    const next = { ...entity };
    rollbackFields.forEach((field) => {
      if (Object.prototype.hasOwnProperty.call(previousEntity, field)) next[field] = previousEntity[field];
      else delete next[field];
    });
    if (isSessionEvent(entity)) {
      const aliases: Record<string, string[]> = {
        name: ['title'],
        date_debut: ['start'],
        date_fin: ['end'],
        sous_traitant: ['resource_id', 'resource_name'],
        mission: ['mission_id', 'mission_name'],
      };
      rollbackFields.flatMap((field) => aliases[field] || []).forEach((alias) => {
        if (Object.prototype.hasOwnProperty.call(previousEntity, alias)) next[alias] = previousEntity[alias];
        else delete next[alias];
      });
    }
    const nextTokens = { ...tokens };
    rollbackFields.forEach((field) => delete nextTokens[field]);
    if (Object.keys(nextTokens).length) next._optimistic_entity_fields = nextTokens;
    else delete next._optimistic_entity_fields;
    if (rollbackFields.includes('pricing_lines')) {
      if (Object.prototype.hasOwnProperty.call(previousEntity, 'pricing_summary')) {
        next.pricing_summary = previousEntity.pricing_summary;
      } else {
        delete next.pricing_summary;
      }
    }
    return next;
  });
}

export function canonicalEntityFromActionResponse(response: unknown, entityType: OptimisticEntityType): JsonRecord | undefined {
  if (!response || typeof response !== 'object' || Array.isArray(response)) return undefined;
  const record = response as JsonRecord;
  const result = record.result && typeof record.result === 'object' && !Array.isArray(record.result)
    ? record.result as JsonRecord
    : {};
  const singular = entityType === 'missions' ? 'mission' : 'session';
  const candidates = [record.entity, record[singular], result.entity, result[singular]];
  const canonical = candidates.find((candidate): candidate is JsonRecord => (
    !!candidate && typeof candidate === 'object' && !Array.isArray(candidate)
  ));
  if (!canonical) return undefined;
  const receiptRevision = record.entity_revision ?? result.entity_revision;
  return receiptRevision !== undefined && canonical.entity_revision === undefined
    ? { ...canonical, entity_revision: receiptRevision }
    : canonical;
}

export function resetEntityMutationOverlaysForTests(): void {
  overlaysByField.clear();
  epoch = 0;
}
